import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import {
  buildMockSession,
  clearMockSession,
  getStoredMockEmail,
  isMockAuthActive,
} from '@/lib/localDevMockAuth';

const rawUrl = import.meta.env.VITE_SUPABASE_URL ?? '';
/** Ohne trailing slash — Vergleich mit Request-URLs aus dem Client */
const envUrl = rawUrl.replace(/\/+$/, '');
const anon = import.meta.env.VITE_SUPABASE_ANON_KEY ?? '';

const disableLocalProxy =
  import.meta.env.VITE_SUPABASE_DISABLE_LOCAL_PROXY === 'true' ||
  import.meta.env.VITE_SUPABASE_DISABLE_LOCAL_PROXY === '1';

/** Dev/Preview: gleicher Host wie die App (Vite-Proxy), damit kein CORS zur Remote-Supabase nötig ist */
function isLikelyLocalFrontend(): boolean {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  if (h === 'localhost' || h === '127.0.0.1' || h === '[::1]') return true;
  if (/^192\.168\.\d{1,3}\.\d{1,3}$/.test(h)) return true;
  if (/^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(h)) return true;
  return /^172\.(1[6-9]|2\d|3[01])\.\d{1,3}\.\d{1,3}$/.test(h);
}

function localDevFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const rewrite =
    !disableLocalProxy && Boolean(envUrl) && typeof window !== 'undefined' && isLikelyLocalFrontend();

  if (!rewrite) {
    return fetch(input, init);
  }

  let urlStr: string;
  if (typeof input === 'string') {
    urlStr = input;
  } else if (input instanceof URL) {
    urlStr = input.href;
  } else {
    urlStr = input.url;
  }

  if (urlStr.startsWith(envUrl)) {
    const proxied = `${window.location.origin}${urlStr.slice(envUrl.length)}`;
    if (typeof input === 'string' || input instanceof URL) {
      return fetch(proxied, init);
    }
    return fetch(new Request(proxied, input), init);
  }

  return fetch(input, init);
}

function applyLocalMockAuthPatches(client: SupabaseClient): void {
  type AuthApi = SupabaseClient['auth'];
  const auth = client.auth as AuthApi & {
    getSession: () => ReturnType<AuthApi['getSession']>;
    getUser: AuthApi['getUser'];
    signOut: AuthApi['signOut'];
    onAuthStateChange: AuthApi['onAuthStateChange'];
  };

  const orig = {
    getSession: auth.getSession.bind(auth),
    getUser: auth.getUser.bind(auth),
    signOut: auth.signOut.bind(auth),
    onAuthStateChange: auth.onAuthStateChange.bind(auth),
  };

  auth.getSession = async () => {
    const mockEmail = getStoredMockEmail();
    if (isMockAuthActive() && mockEmail) {
      return { data: { session: buildMockSession(mockEmail) }, error: null };
    }
    return orig.getSession();
  };

  auth.getUser = async (jwt?: string) => {
    const mockEmail = getStoredMockEmail();
    if (isMockAuthActive() && mockEmail) {
      return { data: { user: buildMockSession(mockEmail).user }, error: null };
    }
    return orig.getUser(jwt);
  };

  auth.signOut = async (opts?: Parameters<AuthApi['signOut']>[0]) => {
    clearMockSession();
    return orig.signOut(opts);
  };

  auth.onAuthStateChange = (callback) => {
    return orig.onAuthStateChange((event, session) => {
      const mockEmail = getStoredMockEmail();
      if (isMockAuthActive() && mockEmail) {
        const mock = buildMockSession(mockEmail);
        if (!session) {
          callback(event === 'INITIAL_SESSION' ? 'INITIAL_SESSION' : event, mock);
          return;
        }
      }
      callback(event, session);
    });
  };
}

export const isSupabaseConfigured = Boolean(envUrl && anon);

const rawClient: SupabaseClient | null = isSupabaseConfigured
  ? createClient(envUrl, anon, { global: { fetch: localDevFetch } })
  : null;

if (rawClient) {
  applyLocalMockAuthPatches(rawClient);
}

export const supabase: SupabaseClient | null = rawClient;
